import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Package, Heart, Users, Mail, ShoppingBag, Megaphone, ExternalLink, KeyRound, BookOpen, Trash2, Image } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

import ProductsTab from "../components/admin/ProductsTab";
import AboutTab from "../components/admin/AboutTab";
import FundsTab from "../components/admin/FundsTab";
import DonationsTab from "../components/admin/DonationsTab";
import SubscribersTab from "../components/admin/SubscribersTab";
import BroadcastsTab from "../components/admin/BroadcastsTab";
import MessagesTab from "../components/admin/MessagesTab";
import OrdersTab from "../components/admin/OrdersTab";
import TrashTab from "../components/admin/TrashTab";
import ChangePasswordDialog from "../components/admin/ChangePasswordDialog";

const TABS = [
  { id: "products", label: "Products", icon: Package, component: ProductsTab },
  { id: "about", label: "About Us", icon: BookOpen, component: AboutTab },
  { id: "funds", label: "Funds Raised", icon: Heart, component: FundsTab },
  { id: "donations", label: "Donations Gallery", icon: Image, component: DonationsTab },
  { id: "subscribers", label: "Subscribers", icon: Users, component: SubscribersTab },
  { id: "broadcasts", label: "Email Blasts", icon: Megaphone, component: BroadcastsTab },
  { id: "messages", label: "Messages", icon: Mail, component: MessagesTab },
  { id: "orders", label: "Orders", icon: ShoppingBag, component: OrdersTab },
  { id: "trash", label: "Trash", icon: Trash2, component: TrashTab },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("products");
  const [showChangePassword, setShowChangePassword] = useState(false);

  const Active = TABS.find((t) => t.id === activeTab)?.component || ProductsTab;

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out. See you soon!");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ backgroundColor: "var(--bg-secondary)" }}>
      {/* Sidebar */}
      <aside
        className="md:w-72 md:min-h-screen md:sticky md:top-0 flex-shrink-0 p-6 md:py-8"
        style={{
          backgroundColor: "var(--bg-primary)",
          borderRight: "1px solid var(--border-soft)",
        }}
        data-testid="admin-sidebar"
      >
        <div className="mb-8">
          <Link to="/" className="font-script text-2xl block" style={{ color: "var(--text-accent)" }}>
            The Giving Stitch
          </Link>
          <p className="text-xs uppercase tracking-[0.25em] mt-1" style={{ color: "var(--text-secondary)" }}>
            Studio Console
          </p>
        </div>

        <nav className="space-y-1 mb-8" data-testid="admin-nav">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors`}
                style={{
                  backgroundColor: isActive ? "var(--brand-sage)" : "transparent",
                  color: isActive ? "#fff" : "var(--text-primary)",
                }}
                data-testid={`admin-tab-${tab.id}`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </nav>

        <div className="mt-auto pt-6" style={{ borderTop: "1px solid var(--border-soft)" }}>
          <div className="px-3 py-2 mb-2">
            <p className="text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
              Signed in as
            </p>
            <p className="text-sm font-semibold mt-0.5" style={{ color: "var(--text-primary)" }}>
              {user?.name || user?.email}
            </p>
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
              {user?.email}
            </p>
          </div>
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors hover:bg-[var(--bg-secondary)]"
            style={{ color: "var(--text-secondary)" }}
            data-testid="admin-view-site-link"
          >
            <ExternalLink size={16} />
            View Site
          </Link>
          <button
            onClick={() => setShowChangePassword(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors hover:bg-[var(--bg-secondary)]"
            style={{ color: "var(--text-secondary)" }}
            data-testid="admin-change-password-button"
          >
            <KeyRound size={16} />
            Change Password
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors hover:bg-[var(--bg-secondary)]"
            style={{ color: "var(--text-accent)" }}
            data-testid="admin-logout-button"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10" data-testid="admin-main">
        <Active />
      </main>

      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
    </div>
  );
}
